import { createFileRoute } from "@tanstack/react-router";
import { LocalServicePage } from "@/components/LocalServicePage";
import hero from "@/assets/smart-home.webp";
import apexTheaterRichImg from "@/assets/Apex_Theater.webp.asset.json";
import { localBusinessSchema, NEIGHBORHOOD_GEO } from "@/lib/localBusinessSchema";

const NEIGHBORHOOD = "Castle Pines";
const SLUG = "castle-pines";
const ZIP = "80108";
const URL = "https://apexav.com/castle-pines/smart-home-installation";

const faqs = [
  {
    q: "How much does a Castle Pines smart home installation cost?",
    a: "Most Castle Pines Village projects land between $50,000 and $250,000 depending on home size and scope. A whole-home automation retrofit on a 6,000 sq ft estate typically runs $60K–$100K. A new-build pre-wire and full integration on a 10,000+ sq ft estate runs $150K+. Every project is fixed-bid before construction begins.",
  },
  {
    q: "Do you handle Castle Pines Village HOA submission?",
    a: "Yes. We're familiar with the Castle Pines Homes Association architectural review process and submit drawings, equipment specs, and exterior fixture details on your behalf. Same for The Village at Castle Pines metro district.",
  },
  {
    q: "Which control platform do you recommend for Castle Pines homes?",
    a: "For most Castle Pines Village estates we lean toward industry-leading control platforms with the scale, dealer network, and reliability for 6,000–15,000 sq ft homes. For lighting-heavy estates we often pair control systems with dedicated lighting platforms for the best of both worlds. We'll recommend the right platform during the free consultation.",
  },
  {
    q: "Can you retrofit my existing Castle Pines Village home without tearing up walls?",
    a: "Yes. We've retrofitted dozens of Castle Pines Village estates with no drywall damage. Modern wireless platforms plus careful fishing of low-voltage cable through existing chases means most retrofits finish with zero patching and minimal disruption.",
  },
  {
    q: "Do you coordinate with my architect, interior designer, and builder?",
    a: "Always. Most Castle Pines projects involve all three. We attend design meetings, mark up drawings with low-voltage routing, coordinate with your electrician on rough-in, and work with your interior designer on keypad finishes, speaker grilles, and equipment placement.",
  },
];

const testimonials = [
  { quote: "Full custom integration on our Castle Pines Village estate — lighting, shades, audio across 14 zones, 6 TVs, security, climate. Apex coordinated with our architect and designer from day one.", name: "Patricia L.", location: "Castle Pines Village" },
  { quote: "We retrofitted our existing Castle Pines home with whole-home automation and smart lighting. Zero drywall damage, finished in 3 weeks, and the app is so much easier than what we had.", name: "Steven B.", location: "Surrey Ridge, Castle Pines" },
  { quote: "Apex handled the HOA submission and coordinated with our builder on the pre-wire. Move-in day everything just worked.", name: "Karen H.", location: "Hidden Pointe, Castle Pines" },
];

export const Route = createFileRoute("/castle-pines/smart-home-installation")({
  head: () => ({
    meta: [
      { title: "Smart Home Installation Castle Pines | Apex AV Denver" },
      { name: "description", content: "Custom smart home design & installation in Castle Pines and Castle Pines Village. Certified smart home integrator. HOA submission included. Free consultation." },
      { name: "keywords", content: "smart home installation Castle Pines, home automation Castle Pines, smart home pre-wire Castle Pines, Castle Pines Village smart home, smart home company Castle Pines" },
      { property: "og:title", content: "Smart Home Installation Castle Pines — Apex AV" },
      { property: "og:description", content: "Custom smart home design & installation in Castle Pines & Castle Pines Village." },
      { property: "og:image", content: `https://apexav.com${apexTheaterRichImg.url}` },
      { property: "og:image:alt", content: "Apex AV custom home theater installation in Denver, Colorado" },
      { property: "og:url", content: URL },
      { property: "og:type", content: "website" },
      { name: "twitter:image", content: `https://apexav.com${apexTheaterRichImg.url}` },
      { name: "twitter:title", content: "Smart Home Installation Castle Pines — Apex AV" },
      { name: "twitter:description", content: "Custom smart home design & installation in Castle Pines & Castle Pines Village." },
    ],
    links: [{ rel: "canonical", href: URL }, { rel: "preload", as: "image", href: hero, fetchPriority: "high" } as never],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Service",
              name: `Smart Home Installation in ${NEIGHBORHOOD}`,
              description: `Custom smart home design and installation in ${NEIGHBORHOOD}, CO.`,
              url: URL,
              provider: { "@id": "https://apexav.com/#organization" },
              areaServed: { "@type": "Place", name: `${NEIGHBORHOOD}, CO ${ZIP}` },
              serviceType: "Smart Home Installation",
              offers: { "@type": "Offer", priceCurrency: "USD", price: "50000" },
            },
            localBusinessSchema({
              neighborhood: NEIGHBORHOOD,
              url: URL,
              image: `https://apexav.com${apexTheaterRichImg.url}`,
              zip: ZIP,
              latitude: NEIGHBORHOOD_GEO[SLUG].latitude,
              longitude: NEIGHBORHOOD_GEO[SLUG].longitude,
              priceFloor: 15000,
              serviceName: "Smart Home Installation",
            }),
            {
              "@type": "FAQPage",
              mainEntity: faqs.map((f) => ({
                "@type": "Question",
                name: f.q,
                acceptedAnswer: { "@type": "Answer", text: f.a },
              })),
            },
          ],
        }),
      },
    ],
  }),
  component: () => (
    <LocalServicePage
      neighborhood={NEIGHBORHOOD}
      neighborhoodSlug={SLUG}
      zip={ZIP}
      service="smart home installation"
      serviceKey="smart-home"
      parentServiceLabel="smart home"
      parentServiceTo="/services/smart-home"
      h1="Smart Home Installation in Castle Pines, CO"
      intro="Whole-home automation integration for Castle Pines Village estates and Castle Pines new builds. Lighting, shades, audio, video, climate, security, and networking — engineered, installed, and supported by one team."
      hero={hero}
      bullets={[
        "Castle Pines Village HOA submission included",
        "Certified smart home integrator",
        "New-construction pre-wire & retrofit",
        "Whole-home smart lighting",
        "Enterprise WiFi for 6,000–15,000 sq ft estates",
        "24/7 remote monitoring & priority support",
      ]}
      microNeighborhoods={["Castle Pines Village", "The Village at Castle Pines", "Surrey Ridge", "Hidden Pointe", "Esperanza", "Glen Oaks"]}
      testimonials={testimonials}
      faqs={faqs}
      bodyParagraphs={[
        {
          heading: "Designed for Castle Pines Village estates",
          body: (
            <>
              <p>
                Castle Pines Village is one of Colorado's premier gated communities — large lots, custom architecture, designer interiors, and homeowners who expect things to just work. Smart home installation here isn't a checkbox on a builder's spec sheet; it's a system that has to integrate with custom millwork, designer keypads, motorized window treatments, outdoor entertaining spaces, and a full security and surveillance perimeter.
              </p>
              <p>
                We've done dozens of estates inside the gates and we know the playbook: HOA submission, architect and designer coordination, fixed-scope drawings, and a finished system that lives invisibly behind the architecture.
              </p>
            </>
          ),
        },
        {
          heading: "What a Castle Pines smart home includes",
          body: (
            <>
              <p>
                A typical Castle Pines Village install covers <strong>whole-home smart lighting</strong> (often 100+ load controls), <strong>motorized shades and drapery</strong> on every south- and west-facing window, <strong>multi-zone audio</strong> (Sonance or Sonos) across 12+ zones including outdoor patios, <strong>video distribution</strong> to every TV, <strong>enterprise-grade WiFi</strong> sized for 6,000–15,000 sq ft, <strong>4K security cameras and access control</strong>, <strong>climate integration</strong> across multiple zones, and a <strong>central control processor</strong> tying it all together.
              </p>
              <p>
                One app. One remote. One scene that says "Goodnight." That's the standard we deliver in every Castle Pines Village home.
              </p>
            </>
          ),
        },
        {
          heading: "Pair it with a dedicated theater",
          body: (
            <>
              <p>
                Most Castle Pines smart home projects we do include a dedicated theater or media room as part of the same scope. See our <a href="/castle-pines/home-theater-installation" className="text-brand-text underline underline-offset-2">Castle Pines home theater installation</a> page for the full theater scope, or browse all <a href="/services" className="text-brand-text underline underline-offset-2">Apex AV services</a>.
              </p>
            </>
          ),
        },
      ]}
    />
  ),
});
